import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    CcComplianceHistory,
    CcComplianceHistoryImportProps,
    CcComplianceItemState,
    CcComplianceRevision,
    CcComplianceTempAnswer,
    CcComplianceTempAnswerVersion,
    CcLibrarySet,
    CcLibrarySetCreate,
    CcLibraryItemsImport,
} from "server/models"
import {
    IDLE, PENDING,
    EDIT, ADD,
    COMPLIANCE, NO,
    WORKFLOW_RERUN,
    WORKFLOW_APPROVED,
    DESC,
} from "const"
import { StateType, CcStateSettingsType, deepCopy, hasProjectAccess } from "client/utils"
import { sort } from "client/store/utils"
import {
    PRM_CC_COMPLIANCE,
    PRM_CC_LIBRARY,
    StoreType,
    CcLibrarySetList,
    ComplianceRevisionValidation,
    CcLibrarySetValidation,

    LoadNestedItems,
    LoadCcComplianceRevisions,
    LoadCcComplianceRevisionData,
    LoadAllCcComplianceItemState,
    LoadAllCcComplianceHistory,

    AddCcLibrarySetModule,
    SaveCcLibrarySet,
    ImportCcLibraryItems,
    ImportCcComplianceHistory,
    CreateCcComplianceRevision,
    CreateCcComplianceState,
    UpdateCcComplianceRevision,
    SaveCcComplianceTempAnswer,
    SaveCcComplianceTempAnswerVersions,

    DeleteCcComplianceRevision,
    DeleteCcLibrarySetModule,

    setCcComplianceRevisionList,
    setCcComplianceRevisionActive,
    setCcComplianceSetIds,
    setCcComplianceSetList,
    setCcComplianceListLoadState,
    setCcComplianceReloadList,

    initComplianceHistory,
} from "client/store"
import {
    TableCustomLevelViewProps,

    SelectDropdownItem,
    FormGroup,
    Label,
    Input,
    Feedback,
    Button,
    SpinnerIcon,
    CopyEmptyIcon,
    CopyFullIcon,
    PencilIcon,

    ConfirmWrapper,
    Confirm,
    ConfirmTitle,
    ConfirmDescription,
    ConfirmBottom,

    PrepareNestedHistory,
    TableGroupedIds,
} from "client/components"

import {
    SetDataWrapper,
    SetName,
    SelectDropdownStyled,
    CopyButtonWrapper,
    CopyButton,
    ConfirmTable,
} from "client/pages/cc/setCustomViewStyled"


const SetCustomView = ({
    groupData,
}: TableCustomLevelViewProps) => {
    const revisionList = useSelector((state: StoreType) => state.ccCompliance.revisionList)
    const revisionActiveList = useSelector((state: StoreType) => state.ccCompliance.revisionActiveList)
    const setList = useSelector((state: StoreType) => state.ccCompliance.setList)
    const setIds = useSelector((state: StoreType) => state.ccCompliance.setIds)

    const moduleLevelList = useSelector((state: StoreType) => state.ccApprovalLevel.moduleLevelList)
    const levelIds = moduleLevelList[1] || []

    const responseList = useSelector((state: StoreType) => state.ccResponse.responseList)

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const set = groupData as CcLibrarySet

    const [revisions, setRevisions] = useState<CcComplianceRevision[]>([])

    const [showDelete, setShowDelete] = useState(false)
    const [showRename, setShowRename] = useState(false)
    const [showRenameSet, setShowRenameSet] = useState(false)
    const [showRerun, setShowRerun] = useState(false)

    const [revisionName, setRevisionName] = useState("")
    const [revisionValidation, setRevisionValidation] = useState<ComplianceRevisionValidation>(null)
    const [renameState, setRenameState] = useState<StateType>(IDLE)

    const [setName, setSetName] = useState(set.name)
    const [setValidation, setSetValidation] = useState<CcLibrarySetValidation>(null)
    const [renameSetState, setRenameSetState] = useState<StateType>(IDLE)

    const activeRevision = revisionActiveList[set.id] || null
    const editComplianceAccess = hasProjectAccess(PRM_CC_COMPLIANCE, EDIT)
    const addLibraryAccess = hasProjectAccess(PRM_CC_LIBRARY, ADD)
    const editLibraryAccess = hasProjectAccess(PRM_CC_LIBRARY, EDIT)

    useEffect(() => {
        // display the last revision on top
        const revisionArray: CcComplianceRevision[] = revisionList[set.id] ? revisionList[set.id].slice() : []
        revisionArray.sort((a, b) => sort(a, b, "number", DESC))
        setRevisions(revisionArray)
    }, [revisionList])

    const changeActiveRevision = (revision: CcComplianceRevision) => {
        dispatch(setCcComplianceListLoadState(PENDING))
        dispatch(setCcComplianceRevisionActive(set.id, revision))

        LoadCcComplianceRevisionData(revision, dispatch).then(() => {
            dispatch(setCcComplianceListLoadState(IDLE))
        })
    }

    const onCancelDelete = () => {
        setShowDelete(false)
    }
    const onConfirmDelete = () => {
        // Delete revision. If this is the only revision it means this compliance set is removed from compliance
        setShowDelete(false)
        dispatch(setCcComplianceListLoadState(PENDING))

        DeleteCcComplianceRevision(activeRevision.id).then(() => {
            LoadCcComplianceRevisions(set.id).then((newRevisions) => {
                if (newRevisions.length > 0) {
                    // set new revision list and load revision data
                    const lastRevision = newRevisions[newRevisions.length - 1]
                    dispatch(setCcComplianceRevisionList(set.id, newRevisions))
                    dispatch(setCcComplianceRevisionActive(set.id, lastRevision))

                    LoadCcComplianceRevisionData(lastRevision, dispatch).then(() => {
                        dispatch(setCcComplianceListLoadState(IDLE))
                    })
                } else {
                    // remove set from compliance
                    DeleteCcLibrarySetModule({
                        setId: set.id,
                        module: COMPLIANCE,
                    }).then(() => {
                        const newSetIds = setIds.filter((setId) => setId !== set.id)
                        const newSetList: CcLibrarySetList = deepCopy(setList)
                        delete newSetList[set.id]

                        dispatch(setCcComplianceSetIds(newSetIds))
                        dispatch(setCcComplianceSetList(newSetList))
                        dispatch(setCcComplianceListLoadState(IDLE))
                    })
                }
            })
        })
    }

    const onCancelRename = () => {
        setShowRename(false)
    }
    const onConfirmRename = () => {
        setRenameState(PENDING)
        UpdateCcComplianceRevision(activeRevision.id, revisionName, lang).then((validation) => {
            if (validation) {
                setRevisionValidation(validation)
                setRenameState(IDLE)
            } else {
                LoadCcComplianceRevisions(set.id).then((newRevisions) => {
                    const newActiveRevision = newRevisions.find((row) => row.id === activeRevision.id)
                    dispatch(setCcComplianceRevisionList(set.id, newRevisions))
                    dispatch(setCcComplianceRevisionActive(set.id, newActiveRevision))

                    setRevisionValidation(null)
                    setRenameState(IDLE)
                    setShowRename(false)
                })
            }
        })
    }

    const onCancelRenameSet = () => {
        setShowRenameSet(false)
    }
    const onConfirmRenameSet = () => {
        setRenameSetState(PENDING)

        const newSetData: CcLibrarySetCreate = {
            ...set,
            name: setName,
        }

        SaveCcLibrarySet(set.id, newSetData, null, lang).then((res) => {
            if (res.validation) {
                setSetValidation(res.validation)
                setRenameSetState(IDLE)
            } else {
                setSetValidation(null)
                setRenameSetState(IDLE)
                setShowRenameSet(false)

                dispatch(setCcComplianceReloadList(false))
                setTimeout(() => {
                    dispatch(setCcComplianceReloadList(true))
                }, 0)
            }
        })
    }

    const onCancelRerun = () => {
        setShowRerun(false)
    }
    const onRerun = (rerunAll: boolean) => {
        setShowRerun(false)
        dispatch(setCcComplianceListLoadState(PENDING))

        LoadCcComplianceRevisions(set.id).then((resRevisions) => {
            CreateCcComplianceRevision(set.id).then((newRevision) => {
                const lastRevision = resRevisions[resRevisions.length - 1]

                Promise.all([
                    LoadAllCcComplianceItemState(lastRevision.id),
                    LoadAllCcComplianceHistory(lastRevision.id),
                ]).then(([stateList, historyList]) => {
                    const promiseArray: Promise<any>[] = []

                    Object.keys(historyList).forEach((index) => {
                        const itemId = Number(index)
                        const itemHistory = historyList[itemId] || []
                        const itemState = stateList[itemId]
                        const settings: CcStateSettingsType = itemState && itemState.settings ? JSON.parse(itemState.settings) : {}

                        let lastHistory: CcComplianceHistory = null
                        let lastResponsibleAnswer: CcComplianceHistory = null
                        for (let i = itemHistory.length - 1; i >= 0; i--) {
                            const history = itemHistory[i]
                            if (history.workflow === WORKFLOW_RERUN) {
                                break
                            }
                            if (!history.systemMessage) {
                                if (!lastHistory) lastHistory = history
                                if (!lastResponsibleAnswer && history.parentId === null) lastResponsibleAnswer = history
                            }
                            if (lastHistory && lastResponsibleAnswer) break
                        }

                        let rerun = false
                        if (rerunAll) {
                            // rerun without condition
                            rerun = true
                        } else if (lastHistory && lastHistory.responseId && lastHistory.workflow === WORKFLOW_APPROVED) {
                            // check only answers by the last verifier level, rerun if not compliant
                            const response = responseList[lastHistory.responseId] || null
                            if (response && response.approved === NO) {
                                rerun = true
                            }
                        }

                        let newState: CcComplianceItemState = {
                            ...itemState,
                            complianceRevisionId: newRevision.id,
                        }

                        // reassign item to first level only if rerun, otherwise - just copy item state and history
                        if (rerun) {
                            const newHistory: CcComplianceHistory = {
                                ...initComplianceHistory,
                                id: null,
                                itemId,
                                complianceRevisionId: newRevision.id,
                                parentId: null,
                                workflow: WORKFLOW_RERUN,
                                systemMessage: true,
                                editorId: userData.userId,
                            }
                            newState = {
                                ...newState,
                                levelId: levelIds[0] || null,
                                verifierId: (levelIds[0] && settings[levelIds[0]]) || null,
                            }

                            itemHistory.push(newHistory)

                            if (lastResponsibleAnswer) {
                                const tempAnswer: CcComplianceTempAnswer = {
                                    itemId,
                                    complianceRevisionId: newRevision.id,
                                    editorId: lastResponsibleAnswer.prevUserId,
                                    responseId: lastResponsibleAnswer.responseId,
                                    answer: lastResponsibleAnswer.answer,
                                    remark: lastResponsibleAnswer.remark,
                                    nextUserId: lastResponsibleAnswer.nextUserId,
                                }
                                const tempVersions: CcComplianceTempAnswerVersion[] = []
                                lastResponsibleAnswer.versions.forEach((version) => {
                                    tempVersions.push({
                                        itemId,
                                        complianceRevisionId: newRevision.id,
                                        editorId: lastResponsibleAnswer.prevUserId,
                                        versionId: version.versionId,
                                    })
                                })

                                promiseArray.push(
                                    SaveCcComplianceTempAnswer(tempAnswer, lang).then(() => (
                                        SaveCcComplianceTempAnswerVersions(tempVersions)
                                    )),
                                )
                            }
                        }

                        const nestedHistory = PrepareNestedHistory(itemHistory)

                        const importData: CcComplianceHistoryImportProps = {
                            nestedHistory,
                            newRevisionId: newRevision.id,
                        }
                        promiseArray.push(
                            ImportCcComplianceHistory(importData),
                            CreateCcComplianceState(newState),
                        )
                    })

                    Promise.all(promiseArray).then(() => {
                        // reload data
                        LoadCcComplianceRevisions(set.id).then((newRevisions) => {
                            const newLastRevision = newRevisions[newRevisions.length - 1]
                            dispatch(setCcComplianceRevisionList(set.id, newRevisions))
                            dispatch(setCcComplianceRevisionActive(set.id, newLastRevision))

                            LoadCcComplianceRevisionData(newLastRevision, dispatch).then(() => {
                                dispatch(setCcComplianceListLoadState(IDLE))
                            })
                        })
                    })
                })
            })
        })
    }

    const onCopy = (copyAll: boolean) => {
        dispatch(setCcComplianceListLoadState(PENDING))

        Promise.all([
            LoadNestedItems(set.id),
            LoadCcComplianceRevisions(set.id).then((res) => {
                const lastRevision = res[res.length - 1]
                return Promise.all([
                    LoadAllCcComplianceItemState(lastRevision.id),
                    LoadAllCcComplianceHistory(lastRevision.id),
                ])
            }),
        ]).then(([nested, [stateList, historyList]]) => {
            const newSetData: CcLibrarySetCreate = {
                originalSetId: set.originalSetId,
                accountId: set.accountId,
                projectId: set.projectId,
                publicId: set.publicId,
                name: `${set.name} (copy)`,
                description: set.description,
                logoId: set.logoId,
                ownerAccount: set.ownerAccount,
                creator: set.creator,
                locked: set.locked,
                access: set.access,
                status: set.status,
                cost: set.cost,
                formal: set.formal,
                default: set.default,
            }

            // project level has unlimited number of sets
            SaveCcLibrarySet(null, newSetData, null, lang).then((res) => {
                const { newSetId } = res
                if (!newSetId) {
                    dispatch(setCcComplianceListLoadState(IDLE))
                    return
                }

                AddCcLibrarySetModule({
                    setId: newSetId,
                    module: COMPLIANCE,
                }).then(() => {
                    CreateCcComplianceRevision(newSetId).then((newRevision) => {
                        const responsibleAnswers: { [itemId: number]: CcComplianceHistory } = {}

                        if (copyAll) {
                            Object.keys(historyList).forEach((index) => {
                                const itemId = Number(index)
                                const itemHistory = historyList[itemId] || []

                                let lastResponsibleAnswer: CcComplianceHistory = null
                                for (let i = itemHistory.length - 1; i >= 0; i--) {
                                    const history = itemHistory[i]
                                    // search independently from "rerun"
                                    if (!history.systemMessage && history.parentId === null) {
                                        lastResponsibleAnswer = history
                                        break
                                    }
                                }

                                if (lastResponsibleAnswer) responsibleAnswers[itemId] = lastResponsibleAnswer
                            })
                        }

                        const nestedUpdated: TableGroupedIds = {
                            ...nested.nested,
                            groupId: null, // loaded root group has "groupId: setId", but for import it should be "groupId: null"
                        }
                        const importData: CcLibraryItemsImport = {
                            nested: nestedUpdated,
                            headingList: nested.headingList,
                            itemList: nested.itemList,
                            newSetId,
                            newRevisionId: newRevision.id,
                            complianceStateFirstLevel: levelIds[0] || null,
                            complianceStateList: stateList,
                            complianceResponsibleAnswers: copyAll ? responsibleAnswers : null,
                        }

                        ImportCcLibraryItems(importData).then(() => {
                            dispatch(setCcComplianceReloadList(false))
                            setTimeout(() => {
                                dispatch(setCcComplianceReloadList(true))
                            }, 0)
                        })
                    })
                })
            })
        })
    }

    return (
        <SetDataWrapper onClick={(e) => e.stopPropagation()}>
            <SetName
                onClick={() => {
                    if (!editLibraryAccess) return
                    setShowRenameSet(true)
                }}
            >
                {set.name}
                {editLibraryAccess && <PencilIcon />}
            </SetName>

            <SelectDropdownStyled
                title={activeRevision ? activeRevision.name : ""}
                row="true"
            >
                {
                    revisions.map((revision) => (
                        <SelectDropdownItem
                            key={revision.id}
                            onClick={() => changeActiveRevision(revision)}
                        >
                            {revision.name}
                        </SelectDropdownItem>
                    ))
                }
            </SelectDropdownStyled>
            {
                editComplianceAccess && (
                    <>
                        <Button
                            row
                            onClick={() => setShowDelete(true)}
                        >
                            {lang.delete}
                        </Button>
                        <Button
                            row
                            variant="success"
                            onClick={() => {
                                setRevisionName(activeRevision.name)
                                setShowRename(true)
                            }}
                        >
                            {lang.rename}
                        </Button>
                        <Button
                            row
                            variant="success"
                            onClick={() => setShowRerun(true)}
                        >
                            {lang.rerun}
                        </Button>
                    </>
                )
            }
            {
                addLibraryAccess && (
                    <CopyButtonWrapper>
                        <CopyButton
                            title={lang.copy}
                            onClick={() => onCopy(false)}
                        >
                            <CopyEmptyIcon />
                        </CopyButton>
                        <CopyButton
                            title={lang.copyAll}
                            onClick={() => onCopy(true)}
                        >
                            <CopyFullIcon />
                        </CopyButton>
                    </CopyButtonWrapper>
                )
            }

            <ConfirmWrapper
                show={showDelete}
                onClose={onCancelDelete}
            >
                <Confirm>
                    <ConfirmTitle>{activeRevision && activeRevision.name}</ConfirmTitle>
                    <ConfirmDescription>{lang.confirmDeleteRevision}</ConfirmDescription>
                    <ConfirmBottom>
                        <Button row onClick={onCancelDelete}>{lang.cancel}</Button>
                        <Button variant="success" row onClick={onConfirmDelete}>{lang.confirm}</Button>
                    </ConfirmBottom>
                </Confirm>
            </ConfirmWrapper>

            <ConfirmWrapper
                show={showRename}
                onClose={onCancelRename}
            >
                <Confirm>
                    <ConfirmTitle>{lang.renameRevision}</ConfirmTitle>
                    <Row>
                        <Col xs={12}>
                            <FormGroup>
                                <Label>{lang.name}</Label>
                                <Input type="text" value={revisionName} onChange={(e) => setRevisionName(e.target.value)} />
                                <Feedback>{revisionValidation && revisionValidation.name}</Feedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    <ConfirmBottom>
                        <Button row onClick={onCancelRename}>{lang.cancel}</Button>
                        <Button variant="success" row onClick={onConfirmRename}>
                            {renameState === PENDING ? <SpinnerIcon /> : lang.confirm}
                        </Button>
                    </ConfirmBottom>
                </Confirm>
            </ConfirmWrapper>

            <ConfirmWrapper
                show={showRenameSet}
                onClose={onCancelRenameSet}
            >
                <Confirm>
                    <ConfirmTitle>{lang.renameSet}</ConfirmTitle>
                    <Row>
                        <Col xs={12}>
                            <FormGroup>
                                <Label>{lang.name}</Label>
                                <Input type="text" value={setName} onChange={(e) => setSetName(e.target.value)} />
                                <Feedback>{setValidation && setValidation.name}</Feedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    <ConfirmBottom>
                        <Button row onClick={onCancelRenameSet}>{lang.cancel}</Button>
                        <Button variant="success" row onClick={onConfirmRenameSet}>
                            {renameSetState === PENDING ? <SpinnerIcon /> : lang.confirm}
                        </Button>
                    </ConfirmBottom>
                </Confirm>
            </ConfirmWrapper>

            <ConfirmWrapper
                show={showRerun}
                onClose={onCancelRerun}
            >
                <Confirm>
                    <ConfirmTitle>{lang.rerun}</ConfirmTitle>
                    <ConfirmTable>
                        <tbody>
                            <tr>
                                <td>
                                    <Button variant="success" onClick={() => onRerun(true)}>{lang.rerunAll}</Button>
                                </td>
                                <td>{lang.rerunAllDescription}</td>
                            </tr>
                            <tr>
                                <td>
                                    <Button variant="success" onClick={() => onRerun(false)}>{lang.rerunNoneCompliantItems}</Button>
                                </td>
                                <td>{lang.rerunNoneCompliantItemsDescription}</td>
                            </tr>
                        </tbody>
                    </ConfirmTable>

                    <ConfirmBottom>
                        <Button onClick={onCancelRerun}>{lang.cancel}</Button>
                    </ConfirmBottom>
                </Confirm>
            </ConfirmWrapper>
        </SetDataWrapper>
    )
}
export default SetCustomView
